import React, { ComponentProps } from 'react'
import { Platform, TextInput, TextInputProps, TextStyle } from 'react-native'

import { styled } from 'nativewind'

import { Stack } from '../layout/Stack'
import { colors } from '../tailwind'
import { B1, B2, B5 } from '../typography/index'

const inputFrameDefaultClass =
  'rounded border font-[Graphie-Book] text-sm leading-5 bg-neutral[white100] focus:font-[Graphie-SemiBold]'
const InputFrame = styled(TextInput, inputFrameDefaultClass, {
  variants: {
    type: {
      primary: 'border-grays-40 focus:border-primary-100',
      success: 'border-success-100 focus:border-primary-100',
      error: 'border-error-100 focus:border-primary-100',
      warning: 'border-warning-100 focus:border-primary-100',
      disabled: 'border-grays-20 bg-grays-5',
    },
    size: {
      sm: 'px-3 py-2',
      md: 'p-4/2',
      lg: 'h-[56] p-4',
    },
  },
  defaultProps: {
    size: 'md',
  },
})

const ErrorText = styled(B5, 'font-normal text-xs	leading-4 pt-2', {
  variants: {
    intent: {
      primary: 'text-grays-60',
      success: 'text-success-150',
      error: 'text-error-100',
      warning: 'text-warning-100',
      disabled: 'text-grays-60',
    },
  },
  defaultProps: {
    intent: 'primary',
  },
})

type InputFrameProps = ComponentProps<typeof InputFrame>
type ErrorTextProps = ComponentProps<typeof ErrorText>

type InputProps = InputFrameProps &
  ErrorTextProps &
  TextInputProps & {
    iconLeft?: React.ReactNode
    iconRight?: React.ReactNode
    showErrorText?: string
    isEditable?: boolean
    frameStyle?: TextStyle
    label?: string
    isRequired?: boolean
    noIconPadding?: boolean
  }

export const BottomSheetInput = React.forwardRef(
  (
    {
      type = 'primary',
      iconLeft,
      iconRight,
      size,
      showErrorText,
      isEditable = true,
      className,
      frameStyle = {},
      label,
      isRequired,
      noIconPadding,
      ...props
    }: InputProps,
    ref
  ) => {
    return (
      <Stack>
        {label && (
          <B2 className=" text-grays-80">
            {label}
            {isRequired && <B1 className="text-error-100 pl-1">*</B1>}
          </B2>
        )}
        {iconLeft ? (
          <Stack
            className={`relative ${
              size === 'md' ? 'top-5' : 'top-6'
            }  z-10 ml-2 mr-auto`}
            style={{
              ...(!noIconPadding && { top: Platform.OS === 'web' ? 36 : 40 }),
            }}
          >
            {iconLeft}
          </Stack>
        ) : null}
        <InputFrame
          editable={isEditable}
          ref={ref}
          type={type}
          size={size}
          selectionColor={colors.grays[50]}
          placeholderTextColor={colors.grays[50]}
          {...props}
          style={[
            {
              paddingLeft:
                iconLeft && Platform.OS === 'web' ? 30 : iconLeft ? 34 : 16,
              paddingRight: iconRight ? 32 : 0,
            },
            frameStyle,
            props.style,
          ]}
        />
        {iconRight ? (
          <Stack
            className={`right-0 z-10 ml-auto`}
            style={{
              ...(noIconPadding
                ? {
                    position: 'absolute',
                    top: 6,
                  }
                : {
                    position: 'relative',
                    bottom: size === 'md' ? 48 : 40,
                  }),
            }}
          >
            {iconRight}
          </Stack>
        ) : null}
        {!!showErrorText && (
          <Stack className={`${noIconPadding ? '-mt-1' : 'mt-2'}`}>
            <ErrorText intent={type}>{showErrorText}</ErrorText>
          </Stack>
        )}
      </Stack>
    )
  }
)
